<template>
    <cod-dialog
        ref="dialog"
        :show.sync="showDialog"
        :drawer="drawer"
        color="primary"
    >
        <!-- Header -->
        <div class="header" slot="header">
            {{ $t('general.cash_register') }}

            <v-spacer />

            <v-menu
                v-if="locale === 'ba'"
                transition="slide-y-transition"
                bottom
            >
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                        <v-icon>far fa-ellipsis-v</v-icon>
                    </v-btn>
                </template>

                <v-list class="order-menu">
                    <v-list-item @click="printReceiptCopy(false)">
                        <v-list-item-action class="mx-3">
                            <v-icon>fal fa-copy</v-icon>
                        </v-list-item-action>
                        <v-list-item-title>
                            {{ $t('cash_register.print_receipt_copy') }}
                        </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="printReceiptCopy(true)">
                        <v-list-item-action class="mx-3">
                            <v-icon>fal fa-print</v-icon>
                        </v-list-item-action>
                        <v-list-item-title>
                            {{ $t('cash_register.print_receipt_refund_copy') }}
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>

        <!-- Content -->
        <div class="cash-register-content" slot="content">
            <template v-if="cashRegister">
                <div class="subtitle-1">
                    {{ $t('cash_register.name') }}: <strong>{{ cashRegister.getFullName() }}</strong>
                </div>
                <div class="subtitle-1">
                    {{ $t('cash_register.registered') }}: <strong>{{ cashRegister.getCreatedAt() | dateTime }}</strong>
                </div>
                <div v-if="!noncashOnly" class="subtitle-1">
                    {{ $t('cash_register.cash') }}: <strong class="error--text">{{ cashRegister.getCash().toFormat() }}</strong>
                </div>

                <v-simple-table v-if="!noncashOnly" class="mt-4">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-left">{{ $t('general.time') }}</th>
                                <th class="text-left">{{ $tc('general.transaction', 1) }}</th>
                                <th class="text-left">{{ $t('general.amount') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>/</td>
                                <td class="success--text">{{ $tc('general.receipt', 2) }}</td>
                                <td>{{ cashRegister.getPaidCash().toFormat() }}</td>
                            </tr>
                            <tr
                                v-for="(transaction, index) in cashRegister.relevantTransactions()"
                                :key="index"
                            >
                                <td>{{ transaction.getIssuedAt() | dateTimeHuman }}</td>
                                <td>
                                    <span v-if="transaction.getAmount().toUnit() >= 0" class="success--text">
                                        {{ $t('general.deposit') }}
                                    </span>
                                    <span v-else class="error--text">
                                        {{ $t('general.widraw') }}
                                    </span>
                                </td>
                                <td>{{ transaction.getAmount().toFormat() }}</td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </template>
        </div>

        <!-- Footer -->
        <template v-if="!noncashOnly" slot="footer">
            <v-btn
                class="flex-1"
                color="primary"
                outlined
                :disabled="!enableDeposit || !allows('allow_cash_register') || !curShift || disabled"
                @click="$emit('deposit'); showDialog = false"
            >
                {{ $t('general.deposit') }}
            </v-btn>

            <v-btn
                class="flex-1"
                color="primary"
                outlined
                :disabled="(enableDeposit && locale === 'me') || !allows('allow_cash_register') || !curShift || disabled"
                @click="$emit('cash-out'); showDialog = false"
            >
                {{ $t('general.cash_out') }}
            </v-btn>
        </template>

    </cod-dialog>
</template>

<script>
import CodDialog from '@/components/CodDialog';
import UserService from '@/services/UserService';
import ApiService from '@/services/ApiService';
import CashRegisterService from '@/services/CashRegisterService';
import FiscalServiceLegacy from '@/services/FiscalServiceLegacy';
import Shift from '@/models/Shift';
import CashRegister from '@/models/CashRegister';
import { required, requiredIf } from 'vuelidate/lib/validators';

export default {
    components: {
        CodDialog
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    created() {
    },

    data() {
        return {
            showDialog: false,
            drawer: null,
            cashRegister: null,
        }
    },

    watch: {
        show(val) {
            this.showDialog = val;
            this.drawer = null;
        },

        showDialog(val) {
            this.$emit('update:show', val);

            if(val) {
                this.cashRegister = CashRegister.current()
            }
        },
    },

    computed: {
        curShift() {
            return Shift.current();
        },

        noncashOnly() {
            return this.tenant && this.tenant.venue && !!this.tenant.venue.settings.noncash_only
        },

        enableDeposit() {
            if(locale === 'sr' || locale === 'ba') return true

            return this.cashRegister && this.cashRegister.depositRequired()
        }
    },

    methods: {
        printReceiptCopy(isRefund = false) {
            this.$confirm(
                this.$t('order.reference_add'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.cancel'),
                    icon: 'fal fa-cash-register',
                    color: 'error',
                    title: !isRefund ? this.$t('cash_register.print_receipt_copy') : this.$t('cash_register.print_receipt_refund_copy'),
                    formFields: [{
                        type: 'text',
                        key: 'refNumber',
                        label: this.$t('receipt.ref_number'),
                        validation: 'required'
                    }]
                },
                async result => {
                    const proceed = result?.hasOwnProperty('confirm') ? result.confirm : result;
                    if (!proceed) return;

                    const printResult = await FiscalServiceLegacy.printReceiptCopy(result.data.refNumber, isRefund)
                    if(printResult.error) {
                        this.$confirm(printResult.error.message || printResult.error.code, {
                            icon: 'fal fa-exclamation-triangle',
                            color: 'error',
                            title: this.$t('order.error.title'),
                        }, async result => {});
                        return
                    }

                    this.$snackbar(this.$t('order.issued'), 'success')
                }
            )
        }
    }
};
</script>

<style lang="scss" scoped>
.header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.cash-register-content {
    padding: 20px;
    min-height: 480px;

    &.needs-registration {
        min-height: 270px;
    }
}

.v-menu {
    z-index: 1000;
}
</style>
